import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  PiArrowsDownUp,
  PiCaretLeft,
  PiCopy,
  PiDotsThree,
  PiDownloadSimple,
  PiFunnel,
  PiPencilSimple,
  PiPlus,
  PiTrash,
  PiX,
} from 'react-icons/pi'
import {
  primaryPillButtonClass,
  secondaryPillButtonClass,
} from 'components/FbUI/secondaryPillButton'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'
import {
  addReportToDashboard,
  createDashboardFromReport,
  listSavedDashboards,
} from './saved-dashboards'
import { resolveThreshold, useKpis } from './kpis'
import { REPORTING_PAGE_SHELL } from './page-shell'
import { MetricView } from './components/MetricView'
import { RankedListView } from './components/RankedListView'
import { ReportChart } from './components/ReportChart'
import { ReportEditorBody } from './components/ReportEditorBody'
import { SourceBadge } from './components/LibraryTable'
import { ReportTable } from './components/ReportTable'
import { QuickFilterBar, QuickState } from './components/QuickFilterBar'
import { TableCard } from './components/TableCard'
import {
  availableViews,
  defaultView,
  ReportView,
  resolveView,
  ViewSwitcher,
} from './components/ViewSwitcher'
import { csvFilename, downloadCsv, toCsv } from './csv'
import {
  deleteSavedView,
  getSavedView,
  saveView,
  updateSavedView,
} from './saved-views'
import { SaveDialog, SaveResult } from './components/SaveDialog'
import { usePipelineSave } from './use-pipeline-save'
import { getTemplate } from './templates'
import { useReportResult } from './use-report-result'
import { SavedView, ViewConfig } from './view-config'
import { PipelineBuilder } from './pipeline/PipelineBuilder'
import { ReportViewer } from './components/ReportViewer'
import { opsToConfig } from './pipeline-compile'
import { runQuery } from './engine'
import { frameOf } from './frames'

/**
 * Detail page for one report. A report is a single component showing exactly
 * one view at a time (table / a chart style / metric).
 *
 * Two modes:
 *  - View (default): static. Only the view switcher — no filters or column
 *    edits. The report reads as a fixed saved object.
 *  - Edit: swaps in the full builder surface (ReportEditorBody — "Show as" +
 *    operations/filters sidebar, editable name/description), then "Save as…"
 *    snapshots the result as a new view (or Cancel discards).
 */
/** Shared row styling for the "More actions" (⋯) overflow menu items. */
const overflowItemClass =
  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-hover'

/** Amber status dot overlaid top-right of a toggle icon to flag an active state. */
function ActiveDot() {
  return (
    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-warning ring-2 ring-surface" />
  )
}

export default function ReportDetailPage() {
  const { viewId = '' } = useParams()
  const initial = getTemplate(viewId) ?? getSavedView(viewId)

  // Pipeline-built reports are rendered by the pipeline builder (op-chain is the
  // source of truth), not the ViewConfig viewer. Dispatch to the right detail
  // surface; each owns its own hooks so neither calls them conditionally.
  if (initial && 'pipeline' in initial && initial.pipeline) {
    return <PipelineReportDetail saved={initial as SavedView} />
  }
  return <ViewConfigReportDetail viewId={viewId} initial={initial} />
}

/** Detail surface for a ViewConfig-based report (template or saved copy). */
function ViewConfigReportDetail({
  viewId,
  initial,
}: {
  viewId: string
  initial: ViewConfig | undefined
}) {
  const navigate = useNavigate()

  // key={viewId} on the route element remounts this component per view,
  // so initializing state from props here is safe (see desktop.tsx).
  const [config, setConfig] = useState<ViewConfig | undefined>(initial)
  const [view, setView] = useState<ReportView>(() =>
    initial ? defaultView(initial) : 'table'
  )
  // Open straight into edit mode when arrived here via "Edit" (e.g. a dashboard
  // tile's kebab → `/reporting/<id>?edit=1`). Read once on mount — the route
  // remounts per viewId (key={viewId}), so this stays correct across reports.
  const [searchParams] = useSearchParams()
  const [editing, setEditing] = useState(searchParams.get('edit') === '1')
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [saveQuickOpen, setSaveQuickOpen] = useState(false)
  // Notion-style: the conditions row is hidden until the header Filter/Sort
  // toggles reveal it. Active filters stay applied while collapsed (the toggle
  // dots flag that), so this controls visibility only — never the query.
  const [showFilters, setShowFilters] = useState(false)
  // Session-layer quick filters/sort the viewer lays over the saved report.
  // Not auto-saved — "Save as report" snapshots the merged config as a new
  // report. Resets per report since the route remounts on viewId change.
  const [quick, setQuick] = useState<QuickState>({ filters: [] })
  const effectiveConfig = useMemo(
    () =>
      config
        ? {
            ...config,
            filters: [...config.filters, ...quick.filters],
            ...(quick.sort ? { sort: quick.sort } : {}),
          }
        : undefined,
    [config, quick]
  )
  const { result } = useReportResult(effectiveConfig)
  const { kpis } = useKpis()

  if (!config || !result || !effectiveConfig) {
    return (
      <div className="p-8">
        <p className="text-fg-secondary">Report not found.</p>
        <Link to="/reporting" className="text-brand">
          Back to reports
        </Link>
      </div>
    )
  }

  // Saving a *saved* report offers overwrite-in-place; a shipped template can
  // only be saved as a new copy (its definition stays put).
  const isSaved = !!getSavedView(viewId)
  const handleSaveNew = ({ name, description }: SaveResult) => {
    const saved = saveView({ ...config, description }, name)
    navigate(`/reporting/${saved.id}`)
  }
  // Persist the base report + the quick filters/sort layer as a brand-new
  // report (the quick overlay becomes the new report's own filters/sort).
  const handleSaveQuick = ({ name, description }: SaveResult) => {
    const saved = saveView({ ...effectiveConfig, description }, name)
    navigate(`/reporting/${saved.id}`)
  }
  const handleOverwrite = ({ name, description }: SaveResult) => {
    const updated = updateSavedView(viewId, { ...config, description }, name)
    if (updated) {
      setConfig(updated)
      setEditing(false)
    }
  }
  // "Save" in edit mode: overwrite in place using the inline-edited name/desc —
  // no naming modal, since the header inputs already capture both.
  const handleSaveInPlace = () =>
    handleOverwrite({ name: config.name, description: config.description })

  // Exports the table exactly as shown: base report + any quick filters/sort.
  const handleExport = () =>
    downloadCsv(csvFilename(config.name), toCsv(effectiveConfig, result))

  // Discards edits (restores the report as it loaded) and exits edit mode.
  const handleCancel = () => {
    setConfig(initial)
    setEditing(false)
  }

  // Dashboards this report can be added to: selecting one appends the report as
  // a tile and opens that dashboard so the new tile is visible.
  const dashboards = listSavedDashboards()

  // Only user-saved reports can be deleted (templates are shipped).
  const handleDelete = () => {
    if (!window.confirm(`Delete "${config.name}"? This can't be undone.`))
      return
    deleteSavedView(viewId)
    navigate('/reporting')
  }

  // Swap any KPI-linked target for its live value before rendering, so editing
  // a linked KPI elsewhere reflects here. (Edit mode renders via ReportViewer,
  // which resolves on its own.)
  const displayConfig = resolveThreshold(config, kpis)

  // Only a row-level table fills the page height; charts/metrics flow naturally.
  // (Edit mode never fills — it scrolls with the editor.)
  const fillTable = !editing && config.kind === 'list' && view === 'table'
  const views = availableViews(config, result)
  // Quick filters/sort: only on a row-level table in view mode (the editor owns
  // shape changes; aggregate sort is metric-based, a separate concern).
  const showQuickBar = !editing && config.kind === 'list' && view === 'table'

  // View mode shows only the view switcher (static); edit mode replaces the
  // body entirely with the full editor (ReportEditorBody).
  const viewToolbar =
    !editing && views.length > 1 ? (
      <div className="ml-auto">
        <ViewSwitcher
          config={config}
          result={result}
          view={view}
          onChange={setView}
        />
      </div>
    ) : undefined

  return (
    <div
      className={cn(
        REPORTING_PAGE_SHELL,
        'gap-4',
        fillTable && 'flex-1 min-h-0'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link
              to="/reporting"
              className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
            >
              <PiCaretLeft size={14} /> Reports
            </Link>
            <SourceBadge source={isSaved ? 'mine' : 'prebuilt'} />
          </div>
          {editing ? (
            <>
              <input
                type="text"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="Untitled report"
                aria-label="Report name"
                className="w-full bg-transparent text-2xl font-bold text-fg outline-none placeholder:text-fg-muted"
              />
              <input
                type="text"
                value={config.description}
                onChange={(e) =>
                  setConfig({ ...config, description: e.target.value })
                }
                placeholder="Add a description (optional)"
                aria-label="Report description"
                className="w-full bg-transparent text-sm text-fg-secondary outline-none placeholder:text-fg-muted"
              />
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-fg">{config.name}</h1>
              {config.description && (
                <p className="text-sm text-fg-secondary">
                  {config.description}
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {editing ? (
            <>
              <button
                type="button"
                onClick={handleCancel}
                className={secondaryPillButtonClass}
              >
                <PiX size={16} /> Cancel
              </button>
              {/* A saved report can overwrite in place (name edits live in the
                  inline header, so no naming modal needed) or fork a copy. A
                  shipped template has nothing to overwrite — only "Save as new". */}
              {isSaved ? (
                <>
                  <button
                    type="button"
                    onClick={() => setDuplicateOpen(true)}
                    className={secondaryPillButtonClass}
                  >
                    Save as new
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveInPlace}
                    className={primaryPillButtonClass}
                  >
                    Save
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setDuplicateOpen(true)}
                  className={primaryPillButtonClass}
                >
                  Save as new
                </button>
              )}
            </>
          ) : (
            <>
              {/* Notion-style Filter / Sort toggles: reveal the conditions row.
                  The amber dot flags that filters/sort are active even when the
                  row is collapsed. */}
              {showQuickBar && (
                <>
                  <button
                    type="button"
                    aria-label="Filter"
                    aria-pressed={showFilters}
                    onClick={() => setShowFilters((v) => !v)}
                    className={cn(
                      'relative inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                      showFilters
                        ? 'border-brand-active text-brand-active'
                        : 'border-line text-fg-secondary hover:text-fg'
                    )}
                  >
                    <PiFunnel size={18} />
                    {quick.filters.length > 0 && <ActiveDot />}
                  </button>
                  <button
                    type="button"
                    aria-label="Sort"
                    aria-pressed={showFilters}
                    onClick={() => setShowFilters((v) => !v)}
                    className={cn(
                      'relative inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                      showFilters
                        ? 'border-brand-active text-brand-active'
                        : 'border-line text-fg-secondary hover:text-fg'
                    )}
                  >
                    <PiArrowsDownUp size={18} />
                    {quick.sort && <ActiveDot />}
                  </button>
                </>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={secondaryPillButtonClass}>
                    <PiPlus size={16} /> Add to…
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-1">
                  <p className="px-2 pb-0.5 pt-1.5 text-xxs font-semibold uppercase tracking-wide text-fg-muted">
                    Add to dashboard
                  </p>
                  {dashboards.length > 0 ? (
                    dashboards.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => {
                          addReportToDashboard(d.id, config)
                          navigate(`/reporting/dashboards/${d.id}`)
                        }}
                        className="flex w-full rounded px-2 py-1.5 text-left text-sm text-fg hover:bg-surface-hover"
                      >
                        {d.name}
                      </button>
                    ))
                  ) : (
                    <p className="px-2 py-1.5 text-sm text-fg-muted">
                      No dashboards yet
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const created = createDashboardFromReport(
                        'New dashboard',
                        config
                      )
                      navigate(`/reporting/dashboards/${created.id}`)
                    }}
                    className="mt-1 flex w-full items-center gap-1.5 rounded border-t border-line px-2 py-2 text-left text-sm text-brand-active hover:bg-surface-hover"
                  >
                    <PiPlus size={14} /> New dashboard…
                  </button>
                </PopoverContent>
              </Popover>
              <button
                type="button"
                onClick={handleExport}
                className={secondaryPillButtonClass}
              >
                <PiDownloadSimple size={16} /> Export CSV
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className={secondaryPillButtonClass}
              >
                <PiPencilSimple size={16} /> Edit
              </button>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="More actions"
                    className={cn(secondaryPillButtonClass, 'px-2')}
                  >
                    <PiDotsThree size={18} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-44 p-1">
                  <button
                    type="button"
                    onClick={() => setDuplicateOpen(true)}
                    className={cn(overflowItemClass, 'text-fg')}
                  >
                    <PiCopy size={16} /> Duplicate
                  </button>
                  {isSaved && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className={cn(overflowItemClass, 'text-error')}
                    >
                      <PiTrash size={16} /> Delete
                    </button>
                  )}
                </PopoverContent>
              </Popover>
            </>
          )}
        </div>
      </div>

      {showQuickBar && showFilters && (
        <QuickFilterBar
          config={config}
          quick={quick}
          onChange={setQuick}
          onSaveAsReport={() => setSaveQuickOpen(true)}
        />
      )}

      {editing ? (
        // Full editor: Show-as switcher + live preview + operations sidebar.
        // mode="edit" → the view switcher only repaints within the report's
        // shape (never reshapes/collapses); refine via the sidebar.
        <ReportEditorBody
          config={config}
          result={result}
          onChange={setConfig}
          mode="edit"
        />
      ) : view === 'metric' ? (
        // A metric is small, so it gets a content-sized stat card; the view
        // switcher (if any) sits as a slim row above it.
        <div className="flex flex-col gap-4">
          {viewToolbar && (
            <div className="flex flex-wrap items-end gap-4">{viewToolbar}</div>
          )}
          <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-5">
            <MetricView config={displayConfig} result={result} />
          </div>
        </div>
      ) : (
        <TableCard fill={fillTable} toolbar={viewToolbar}>
          {view === 'table' ? (
            <ReportTable
              fill={fillTable}
              config={effectiveConfig}
              result={result}
            />
          ) : view === 'ranked' ? (
            <div className="p-4">
              <RankedListView config={config} result={result} />
            </div>
          ) : (
            // No in-chart metric switcher here: a report commits to one measure.
            // Charting a different measure is a different report (build / edit →
            // Save as), not a toggle on this static one.
            <ReportChart
              bare
              chartType={view}
              config={displayConfig}
              result={result}
            />
          )}
        </TableCard>
      )}

      <SaveDialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        kind="report"
        titleOverride="Duplicate report"
        acceptTextOverride="Duplicate"
        defaultName={`${config.name} (copy)`}
        defaultDescription={config.description}
        onSaveNew={handleSaveNew}
      />

      <SaveDialog
        open={saveQuickOpen}
        onClose={() => setSaveQuickOpen(false)}
        kind="report"
        titleOverride="Save as new report"
        defaultName={`${config.name} (filtered)`}
        defaultDescription={config.description}
        onSaveNew={handleSaveQuick}
      />
    </div>
  )
}

/**
 * Detail surface for a pipeline-built report. The op-chain is editable in place
 * (the builder is the same one used to create it); "Save report…" snapshots the
 * current chain as a new saved report. Delete removes this one.
 */
function PipelineReportDetail({ saved: initialSaved }: { saved: SavedView }) {
  const navigate = useNavigate()
  const { requestSave, dialog } = usePipelineSave(navigate)
  // Local copy so an in-place overwrite (rename / edited ops) reflects without a
  // remount — the route keys on viewId, which is unchanged by an overwrite.
  const [saved, setSaved] = useState(initialSaved)
  const [editing, setEditing] = useState(false)
  const [view, setView] = useState<ReportView | null>(null)

  const handleDelete = () => {
    if (!window.confirm(`Delete "${saved.name}"? This can't be undone.`)) return
    deleteSavedView(saved.id)
    navigate('/reporting')
  }

  // The op-chain is the source of truth; compile it to a ViewConfig and render
  // through the shared viewer so a pipeline report reads like any other report.
  const frame = saved.pipelineFrame ?? 'deal'
  const config = opsToConfig(saved.pipeline ?? [], frame)
  const result = runQuery(config, frameOf(frame))
  const effView = resolveView(config, result, view)

  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-4')}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link
              to="/reporting"
              className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
            >
              <PiCaretLeft size={14} /> Reports
            </Link>
            {/* Pipeline reports are always user-saved (no shipped ones). */}
            <SourceBadge source="mine" />
          </div>
          <h1 className="text-2xl font-bold text-fg">{saved.name}</h1>
          {saved.description && (
            <p className="text-sm text-fg-secondary">{saved.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            className={secondaryPillButtonClass}
          >
            {editing ? (
              <>
                <PiX size={16} /> Done editing
              </>
            ) : (
              <>
                <PiPencilSimple size={16} /> Edit
              </>
            )}
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className={cn(secondaryPillButtonClass, 'px-2')}
              >
                <PiDotsThree size={18} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-44 p-1">
              <button
                type="button"
                onClick={() =>
                  requestSave(saved.pipeline ?? [], frame, {
                    defaultName: `${saved.name} (copy)`,
                  })
                }
                className={cn(overflowItemClass, 'text-fg')}
              >
                <PiCopy size={16} /> Duplicate
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className={cn(overflowItemClass, 'text-error')}
              >
                <PiTrash size={16} /> Delete
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {editing ? (
        <PipelineBuilder
          initialOps={saved.pipeline}
          initialFrame={saved.pipelineFrame}
          // Saving offers "Save" (overwrite in place — renames this report and
          // persists any edited ops) or "Save as new" (a copy).
          onSave={(ops, frame) =>
            requestSave(ops, frame, {
              defaultName: saved.name,
              existingId: saved.id,
              onSaved: (updated) => {
                setSaved(updated)
                setEditing(false)
              },
            })
          }
        />
      ) : (
        <ReportViewer
          config={config}
          result={result}
          view={effView}
          onViewChange={setView}
        />
      )}
      {dialog}
    </div>
  )
}
